import { Activity, ActivityStatus, Client, Currency, GroupByOption } from '../types/GrooverTypes'

export type PublicActivitiesViewMode = 'grid' | 'high-grid' | 'week' | 'list' | 'compact'

export type PublicActivitiesFilterParams = {
  filter?: string
  danceStyle?: string
  activityType?: string
  period?: string
  periodId?: string
  showSubEvents?: boolean
  showParentEvent?: boolean
  instructor?: string
  locationId?: string
  excludeDropinOnly?: boolean
  showDraftActivities?: boolean
}

export type PublicActivitiesQueryParams = PublicActivitiesFilterParams & {
  group?: GroupByOption | string
  cityName?: string
  activityType?: string
  groupSorting?: string
  viewMode?: PublicActivitiesViewMode | string
  hideFilter?: boolean
  disableMonthGrouping?: boolean
  lang?: string
}

const TYPE_ORDER = ['Course', 'Other', 'Occasion']

const DEFAULT_TYPE_LABELS: Record<string, string> = {
  Other: 'Other',
  Course: 'Course',
  Occasion: 'Occasion',
  Event: 'Event'
}

export const formatPublicActivityPrice = (priceInCents: number, currency: Currency): string => {
  const priceInMainUnit = Math.round(priceInCents / 100)
  return `${priceInMainUnit} ${currency}`
}

export const formatDateSwedish = (date: string) => {
  return new Date(date)
    .toLocaleString('sv-SE', { month: 'long', year: 'numeric' })
    .replace(/^\w/, (c) => c.toUpperCase())
}

export const getLocalizedActivityTypeServer = (type: string, client: Client): string => {
  const types = {
    Other: client?.typeTranslations?.Other || DEFAULT_TYPE_LABELS.Other,
    Course: client?.typeTranslations?.Course || DEFAULT_TYPE_LABELS.Course,
    Occasion: client?.typeTranslations?.Occasion || DEFAULT_TYPE_LABELS.Occasion,
    Event: client?.typeTranslations?.Event || DEFAULT_TYPE_LABELS.Event
  }
  return types[type as keyof typeof types] || '-'
}

const getLocalizedActivityTypeTranslatedServer = (type: string, client: Client) => {
  const types = {
    Other: client?.typeTranslations?.Other || DEFAULT_TYPE_LABELS.Other,
    Course: client?.typeTranslations?.Course || DEFAULT_TYPE_LABELS.Course,
    Occasion: client?.typeTranslations?.Occasion || DEFAULT_TYPE_LABELS.Occasion,
    Event: client?.typeTranslations?.Event || DEFAULT_TYPE_LABELS.Event
  }

  const reverseTypeMap = Object.entries(types).reduce(
    (acc, [original, translated]) => {
      acc[translated] = original
      return acc
    },
    {} as Record<string, string>
  )

  const originalKey = reverseTypeMap[type] || type
  return { translated: type, original: originalKey }
}

const minManualSortIndexInActivities = (activities: Activity[]): number => {
  let min = Number.POSITIVE_INFINITY
  for (const a of activities) {
    if (typeof a.manualSortIndex === 'number') {
      min = Math.min(min, a.manualSortIndex)
    }
  }
  return min
}

type PrimaryGroupEntry = [groupKey: string, activities: Activity[]]

const comparePrimaryGroupsDefault = ([keyA]: PrimaryGroupEntry, [keyB]: PrimaryGroupEntry, client: Client): number => {
  const { original: originalA, translated: typeA } = getLocalizedActivityTypeTranslatedServer(keyA, client)
  const { original: originalB, translated: typeB } = getLocalizedActivityTypeTranslatedServer(keyB, client)
  const indexA = TYPE_ORDER.indexOf(originalA)
  const indexB = TYPE_ORDER.indexOf(originalB)

  if (indexA === -1 && indexB === -1) {
    return typeA.localeCompare(typeB)
  }

  return indexA - indexB || typeA.localeCompare(typeB)
}

const comparePrimaryGroupsManual = (a: PrimaryGroupEntry, b: PrimaryGroupEntry): number => {
  const rankA = minManualSortIndexInActivities(a[1])
  const rankB = minManualSortIndexInActivities(b[1])
  if (rankA !== rankB) {
    return rankA - rankB
  }
  return a[0].localeCompare(b[0])
}

const sortPrimaryGroupEntries = (
  entries: PrimaryGroupEntry[],
  groupBy: GroupByOption,
  groupSorting: string,
  client: Client
): PrimaryGroupEntry[] => {
  if (groupBy === GroupByOption.StartDate) {
    if (groupSorting === 'manual') {
      return [...entries].sort(comparePrimaryGroupsManual)
    }
    return [...entries].sort(([, activitiesA], [, activitiesB]) => {
      const dateA = Math.min(...activitiesA.map((a) => new Date(a.startDate).getTime()))
      const dateB = Math.min(...activitiesB.map((b) => new Date(b.startDate).getTime()))
      return dateA - dateB
    })
  }
  if (groupSorting === 'manual') {
    return [...entries].sort(comparePrimaryGroupsManual)
  }
  return [...entries].sort((a, b) => comparePrimaryGroupsDefault(a, b, client))
}

export const sortPublicActivities = (activities: Activity[], sortBy: string): Activity[] => {
  return [...activities].sort((a, b) => {
    switch (sortBy) {
      case 'manual': {
        const aIndex = a.manualSortIndex
        const bIndex = b.manualSortIndex
        const aHasIndex = typeof aIndex === 'number'
        const bHasIndex = typeof bIndex === 'number'

        if (aHasIndex && !bHasIndex) return -1
        if (!aHasIndex && bHasIndex) return 1

        if (aHasIndex && bHasIndex && aIndex !== bIndex) {
          return aIndex - bIndex
        }

        const dateA = new Date(a.startDate).getTime()
        const dateB = new Date(b.startDate).getTime()
        if (dateA !== dateB) return dateA - dateB

        const timeA = (a.time || '00:00').split(':').map(Number)
        const timeB = (b.time || '00:00').split(':').map(Number)
        const timeCmp = timeA[0] * 60 + timeA[1] - (timeB[0] * 60 + timeB[1])
        if (timeCmp !== 0) return timeCmp

        return (a.name || '').localeCompare(b.name || '')
      }
      case 'level':
        return (a.level || '').localeCompare(b.level || '')
      case 'startdate': {
        const dateA = new Date(a.startDate).getTime()
        const dateB = new Date(b.startDate).getTime()

        if (dateA !== dateB) {
          return dateA - dateB
        }

        const timeA = (a.time || '00:00').split(':').map(Number)
        const timeB = (b.time || '00:00').split(':').map(Number)

        return timeA[0] * 60 + timeA[1] - (timeB[0] * 60 + timeB[1])
      }
      case 'name':
        return (a.name || '').localeCompare(b.name || '')
      default:
        return 0
    }
  })
}

export const filterByDanceStyle = (activities: Activity[], danceStyle: string) => {
  return activities.filter((activity) => activity.style?.toLowerCase() === danceStyle.toLowerCase())
}

export const filterPublicActivities = (activities: Activity[], params: PublicActivitiesFilterParams): Activity[] => {
  let filtered = activities

  if (!params.showSubEvents) {
    filtered = filtered.filter((activity) => !activity.subeventParentId)
  }

  if (params.showParentEvent === false) {
    filtered = filtered.filter((activity) => activity.eventType !== 'Parentevent')
  }

  if (params.filter) {
    const filterLower = params.filter.toLowerCase()
    filtered = filtered.filter((activity) => activity.name.toLowerCase().includes(filterLower))
  }

  if (params.danceStyle) {
    filtered = filterByDanceStyle(filtered, params.danceStyle)
  }

  if (params.activityType) {
    filtered = filtered.filter((activity) => activity.type === params.activityType)
  }

  if (params.periodId) {
    filtered = filtered.filter((activity) => activity.periodId === params.periodId)
  } else if (params.period) {
    filtered = filtered.filter((activity) => activity.period === params.period)
  }

  if (params.instructor) {
    const q = params.instructor.toLowerCase()
    filtered = filtered.filter((activity) => activity.instructorsNames?.toLowerCase().includes(q))
  }

  if (params.locationId) {
    filtered = filtered.filter((activity) => activity.locationId === params.locationId)
  }

  if (params.excludeDropinOnly) {
    filtered = filtered.filter((activity) => !activity.dropinOnly)
  }

  return filtered
}

export const groupAndSortPublicActivities = (
  activities: Activity[],
  groupBy: GroupByOption,
  groupSorting: string,
  cityName: string,
  client: Client,
  disableMonthGrouping = false
) => {
  const filteredActivities = cityName
    ? activities.filter((a) => a.city?.toLowerCase() === cityName.toLowerCase())
    : activities

  if (groupBy === GroupByOption.None) {
    const sortedActivities = sortPublicActivities(filteredActivities, groupSorting)
    return { '': { '': sortedActivities } }
  }

  const primaryGrouped = filteredActivities.reduce((acc: { [key: string]: Activity[] }, activity) => {
    let groupKey = ''

    switch (groupBy) {
      case GroupByOption.StartDate:
        groupKey = formatDateSwedish(activity.startDate || '')
        break
      case GroupByOption.Type:
        groupKey = getLocalizedActivityTypeServer(activity.type, client)
        break
      case GroupByOption.City:
        groupKey = activity.city || 'No City'
        break
      case GroupByOption.Style:
        groupKey = activity.style || 'No Style'
        break
      case GroupByOption.Period: {
        const periodEntry = activity.periodId ? client?.periods?.find((p) => p.id === activity.periodId) : null
        groupKey = periodEntry?.periodName ?? activity.period ?? 'No Period'
        break
      }
      case GroupByOption.Instructors:
        groupKey = activity.instructorsNames || 'No Instructors'
        break
      case GroupByOption.Level:
        groupKey = activity.level || 'No Level'
        break
      case GroupByOption.Location: {
        if (activity.locationId && client?.locations) {
          const match = client.locations.find((l) => l.id === activity.locationId)
          if (match?.name) {
            groupKey = match.name
            break
          }
        }
        groupKey = activity.city || 'No Location'
        break
      }
      default:
        groupKey = 'Other'
    }

    if (!acc[groupKey]) acc[groupKey] = []
    acc[groupKey].push(activity)

    return acc
  }, {})

  if (groupBy === GroupByOption.StartDate) {
    const sortedGroups = sortPrimaryGroupEntries(Object.entries(primaryGrouped), groupBy, groupSorting, client)

    return Object.fromEntries(
      sortedGroups.map(([key, groupActivities]) => [
        key,
        { [key]: sortPublicActivities(groupActivities, groupSorting) }
      ])
    )
  }

  const sortedPrimaryGroups = sortPrimaryGroupEntries(Object.entries(primaryGrouped), groupBy, groupSorting, client)

  if (disableMonthGrouping) {
    return Object.fromEntries(
      sortedPrimaryGroups.map(([key, acts]) => [key, { [key]: sortPublicActivities(acts, groupSorting) }])
    )
  }

  const finalGrouped: Record<string, Record<string, Activity[]>> = Object.fromEntries(
    sortedPrimaryGroups
      .map(([groupKey, groupActivities]) => {
        const subGrouped = groupActivities.reduce((acc: { [key: string]: Activity[] }, activity) => {
          const subGroupKey = formatDateSwedish(activity.startDate || '')

          if (!acc[subGroupKey]) acc[subGroupKey] = []
          acc[subGroupKey].push(activity)

          return acc
        }, {})

        Object.keys(subGrouped).forEach((key) => {
          subGrouped[key] = sortPublicActivities(subGrouped[key], groupSorting)
        })

        const sortedSubGroups = Object.entries(subGrouped).sort(([, activitiesA], [, activitiesB]) => {
          const dateA = Math.min(...activitiesA.map((a) => new Date(a.startDate).getTime()))
          const dateB = Math.min(...activitiesB.map((b) => new Date(b.startDate).getTime()))

          if (dateA !== dateB) {
            return dateA - dateB
          }

          const timeA = Math.min(
            ...activitiesA.map((a) => {
              const [hours, minutes] = (a.time || '00:00').split(':').map(Number)
              return hours * 60 + minutes
            })
          )
          const timeB = Math.min(
            ...activitiesB.map((b) => {
              const [hours, minutes] = (b.time || '00:00').split(':').map(Number)
              return hours * 60 + minutes
            })
          )

          return timeA - timeB
        })

        return [groupKey, Object.fromEntries(sortedSubGroups)]
      })
      .sort((a, b) => {
        const [keyA, nestedA] = a as [string, Record<string, Activity[]>]
        const [keyB, nestedB] = b as [string, Record<string, Activity[]>]
        if (groupBy === GroupByOption.Type) return 0
        if (groupSorting === 'manual') {
          const rankA = minManualSortIndexInActivities(Object.values(nestedA).flat())
          const rankB = minManualSortIndexInActivities(Object.values(nestedB).flat())
          if (rankA !== rankB) return rankA - rankB
          return keyA.localeCompare(keyB)
        }
        const dateA = Math.min(
          ...Object.values(nestedA)
            .flat()
            .map((act) => new Date(act.startDate).getTime())
        )
        const dateB = Math.min(
          ...Object.values(nestedB)
            .flat()
            .map((act) => new Date(act.startDate).getTime())
        )
        return dateA - dateB
      })
  )

  return finalGrouped
}

export const parsePublicActivitiesQueryParams = (searchParams: URLSearchParams): PublicActivitiesQueryParams => {
  return {
    filter: searchParams.get('filter') ?? undefined,
    group: (searchParams.get('group') as GroupByOption) || GroupByOption.Style,
    cityName: searchParams.get('cityName') ?? undefined,
    danceStyle: searchParams.get('danceStyle') ?? undefined,
    activityType: searchParams.get('activityType') ?? undefined,
    groupSorting: searchParams.get('groupSorting') ?? 'startdate',
    viewMode: (searchParams.get('viewMode') as PublicActivitiesViewMode) ?? 'grid',
    hideFilter: searchParams.get('hideFilter') === 'true',
    period: searchParams.get('period') ?? undefined,
    periodId: searchParams.get('periodId') ?? undefined,
    showSubEvents: searchParams.get('showSubEvents') === 'true',
    showParentEvent: searchParams.get('showParentEvent') !== 'false',
    disableMonthGrouping: searchParams.get('disableMonthGrouping') === 'true',
    lang: searchParams.get('lang') ?? undefined,
    instructor: searchParams.get('instructor') ?? undefined,
    locationId: searchParams.get('locationId') ?? undefined,
    excludeDropinOnly: searchParams.get('excludeDropinOnly') === 'true',
    showDraftActivities: searchParams.get('showDraftActivities') === 'true'
  }
}

export const filterActivePublicActivities = (
  activities: Activity[],
  options?: { includeDrafts?: boolean }
): Activity[] => {
  return activities.filter(
    (activity) =>
      activity.status === ActivityStatus.Active ||
      (options?.includeDrafts === true && activity.status === ActivityStatus.Draft)
  )
}

/** Structured snapshot for A/B DOM parity comparison (CSR vs SSR). */
export type PublicActivitiesListingSnapshot = {
  activityIds: string[]
  groupHeadings: string[]
}

export const buildPublicActivitiesListingSnapshot = (
  groupedActivities: Record<string, Record<string, Activity[]>>
): PublicActivitiesListingSnapshot => {
  const activityIds: string[] = []
  const groupHeadings: string[] = []

  for (const [groupKey, subGroups] of Object.entries(groupedActivities)) {
    if (groupKey) groupHeadings.push(groupKey)
    for (const [subGroupKey, activities] of Object.entries(subGroups)) {
      if (subGroupKey && subGroupKey !== groupKey) groupHeadings.push(subGroupKey)
      for (const activity of activities) {
        if (activity.id) activityIds.push(activity.id)
      }
    }
  }

  return { activityIds, groupHeadings }
}
